import { ReactElement, useEffect, useState } from 'react';
import {
  Datagrid,
  DateInput,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  required,
  SelectInput,
  TextInput,
  TopToolbar,
  useNotify,
  useTranslate,
} from 'react-admin';
import { Box, Button, Card, Chip, Dialog, Link, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import {
  HotWalletItem,
  listHotWallets,
  listProviders,
  ProviderItem,
} from '../../../services/xbitRelayService';
import {
  DEFAULT_DEPOSITS_PROVIDER,
  DEPOSIT_STATUSES,
  XBIT_RELAY_DEPOSITS_PERMISSION_RESOURCE,
} from './constants';
import {
  chainSymbol,
  explorerTxUrl,
  isSubmittedTxOnchain,
  statusColor,
  truncate,
} from './helpers';
import { RetryDialog } from './RetryDialog';
import { ExportButton } from './ExportButton';
import './init';

const DepositsListActions = ({
  providers,
  chains,
}: {
  providers: ProviderItem[];
  chains: HotWalletItem[];
}) => (
  <TopToolbar
    sx={{
      minHeight: 90,
      alignItems: 'center',
      px: 3,
      pt: 1,
      overflow: 'visible',
      width: '100%',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
        '& form': { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%' },
        '& .ra-input': { mt: 0, mb: 0 },
        '& .MuiFormControl-root': { mt: 0, mb: 0 },
        '& .MuiInputBase-root': { minHeight: 40 },
      }}
    >
      <FilterForm
        filters={[
          <SelectInput
            key="provider"
            source="provider"
            label="module.xbitRelayDeposits.provider"
            choices={providers.map((p) => ({ id: p.provider, name: p.provider }))}
            alwaysOn
            size="small"
            sx={{ minWidth: 200 }}
            validate={required()}
            isRequired
          />,
          <SelectInput
            key="status"
            source="status"
            label="module.xbitRelayDeposits.status"
            choices={DEPOSIT_STATUSES.map((s) => ({ id: s, name: s }))}
            alwaysOn
            resettable
            emptyText="ALL"
            size="small"
            sx={{ minWidth: 160 }}
          />,
          <SelectInput
            key="chainId"
            source="chainId"
            label="module.xbitRelayDeposits.chain"
            choices={chains.map((c) => ({ id: c.chainId, name: `${c.chainName} (${c.chainId})` }))}
            alwaysOn
            resettable
            emptyText="ALL"
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <TextInput
            key="txHash"
            source="txHash"
            label="module.xbitRelayDeposits.txHash"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <TextInput
            key="toAddress"
            source="toAddress"
            label="module.xbitRelayDeposits.toAddress"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <TextInput
            key="userId"
            source="userId"
            label="module.xbitRelayDeposits.userId"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 240 }}
          />,
          <TextInput
            key="userAddress"
            source="userAddress"
            label="module.xbitRelayDeposits.userAddress"
            resettable
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <TextInput
            key="token"
            source="token"
            label="module.xbitRelayDeposits.token"
            resettable
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <DateInput
            key="timeFrom"
            source="timeFrom"
            label="common.fromTime"
            size="small"
            parse={(value: string) => (value ? dayjs(value).startOf('day').valueOf() : undefined)}
            format={(value: any) => (value ? dayjs(Number(value)).format('YYYY-MM-DD') : '')}
            sx={{ minWidth: 180 }}
          />,
          <DateInput
            key="timeTo"
            source="timeTo"
            label="common.toTime"
            size="small"
            parse={(value: string) => (value ? dayjs(value).endOf('day').valueOf() : undefined)}
            format={(value: any) => (value ? dayjs(Number(value)).format('YYYY-MM-DD') : '')}
            sx={{ minWidth: 180 }}
          />,
        ]}
      />
    </Box>
    <ExportButton />
  </TopToolbar>
);

const XbitRelayDepositsList = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [providers, setProviders] = useState<ProviderItem[]>([]);
  const [chains, setChains] = useState<HotWalletItem[]>([]);
  const [popup, setPopup] = useState<ReactElement>();

  useEffect(() => {
    listProviders()
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    listHotWallets(DEFAULT_DEPOSITS_PROVIDER)
      .then((res) => setChains(res.data?.wallets ?? []))
      .catch(() => setChains([]));
  }, []);

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('Copied to clipboard', { type: 'info' });
  };

  return (
    <DashboardContent>
      <PermissionGuard
        resource={XBIT_RELAY_DEPOSITS_PERMISSION_RESOURCE}
        action="read"
        fallback={<ErrorPage403 />}
      >
        <CustomBreadcrumbs
          heading={t('module.xbitRelayDeposits.title')}
          links={[
            { name: t('resource.xbitRelay'), href: '#' },
            { name: t('module.xbitRelayDeposits.title') },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={<DepositsListActions providers={providers} chains={chains} />}
            empty={false}
            perPage={20}
            exporter={false}
            filterDefaultValues={{ provider: DEFAULT_DEPOSITS_PROVIDER }}
            pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
            sort={{ field: 'createdAtUnixMs', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={<EmptyContent />}
                sx={{
                  '& .RaDatagrid-headerCell': { fontWeight: 600 },
                  '& .RaDatagrid-rowCell': { py: 1.25 },
                }}
              >
                <FunctionField
                  label="module.xbitRelayDeposits.time"
                  sortable={false}
                  render={(record: any) =>
                    record.createdAtUnixMs ? (
                      <Tooltip
                        title={dayjs(Number(record.createdAtUnixMs)).format(
                          'YYYY-MM-DD HH:mm:ss',
                        )}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                        >
                          {dayjs(Number(record.createdAtUnixMs)).format('MM-DD HH:mm')}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label="module.xbitRelayDeposits.chain"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={String(record.chainId)}>
                      <Chip
                        label={chainSymbol(record.chainId)}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.status"
                  sortable={false}
                  render={(record: any) => (
                    <Chip
                      label={record.status || '-'}
                      size="small"
                      color={statusColor(record.status) as any}
                      variant={record.status === 'PENDING' ? 'outlined' : 'filled'}
                    />
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.errorMsg"
                  sortable={false}
                  render={(record: any) => {
                    if (!record.errorMsg) return '-';
                    return (
                      <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-line' }}>
                        {record.errorMsg}
                      </Typography>
                    );
                  }}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.token"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {record.tokenSymbol || '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.amount"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record.amount || '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.toAddress"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={record.toAddress || ''}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        onClick={() => copy(record.toAddress)}
                      >
                        {truncate(record.toAddress)}
                      </Typography>
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.targetAddress"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={record.targetAddress || ''}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        onClick={() => copy(record.targetAddress)}
                      >
                        {truncate(record.targetAddress)}
                      </Typography>
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.depositTransaction"
                  sortable={false}
                  render={(record: any) => {
                    if (!record.txHash) return '-';
                    const url = explorerTxUrl(record.chainId, record.txHash);
                    if (!url) {
                      return (
                        <Tooltip title={record.txHash}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                            onClick={() => copy(record.txHash)}
                          >
                            {truncate(record.txHash)}
                          </Typography>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip title={record.txHash}>
                        <Link
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {truncate(record.txHash)}
                        </Link>
                      </Tooltip>
                    );
                  }}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.submittedTxHash"
                  sortable={false}
                  render={(record: any) => {
                    if (!record.submittedTxHash) return '-';
                    const url = explorerTxUrl(record.chainId, record.submittedTxHash);
                    if (!url || !isSubmittedTxOnchain(record)) {
                      return (
                        <Tooltip title={record.submittedTxHash}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                            onClick={() => copy(record.submittedTxHash)}
                          >
                            {truncate(record.submittedTxHash)}
                          </Typography>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip title={record.submittedTxHash}>
                        <Link
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {truncate(record.submittedTxHash)}
                        </Link>
                      </Tooltip>
                    );
                  }}
                />
                <FunctionField
                  label="module.xbitRelayDeposits.userId"
                  sortable={false}
                  render={(record: any) =>
                    record.userId ? (
                      <Tooltip title={record.userId}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => copy(record.userId)}
                        >
                          {truncate(record.userId)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label=""
                  sortable={false}
                  render={(record: any) => {
                    if (record.status !== 'FAILED') return null;
                    return (
                      <PermissionGuard
                        resource={XBIT_RELAY_DEPOSITS_PERMISSION_RESOURCE}
                        action="retry"
                        fallback={<></>}
                      >
                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            onClick={(e) => {
                              e.preventDefault();
                              setPopup(
                                <RetryDialog
                                  record={record}
                                  onClose={() => setPopup(undefined)}
                                />,
                              );
                            }}
                          >
                            {t('module.xbitRelayDeposits.retry')}
                          </Button>
                        </Stack>
                      </PermissionGuard>
                    );
                  }}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog
          open={Boolean(popup)}
          onClose={() => setPopup(undefined)}
          maxWidth="sm"
          fullWidth
        >
          {popup}
        </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XbitRelayDepositsList;
